import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChartBarIcon,
  ChevronDownIcon,
  ComputerDesktopIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  MoonIcon,
  QuestionMarkCircleIcon,
  ShoppingBagIcon,
  SunIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LOGO_URL } from '../Marketing/MarketingNav';

const THEME_ORDER = ['light', 'dark', 'system'];
const THEME_ICON = { light: SunIcon, dark: MoonIcon, system: ComputerDesktopIcon };

export const initials = (user) => (user?.full_name || user?.email || '?')
  .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

// Top navbar of the redesigned shell: menu toggle, brand, store shortcut, theme and account
const Header2 = ({ onMenu, focus = false }) => {
  const { user, logout } = useAuth();
  const { theme, changeTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const ThemeIcon = THEME_ICON[theme] || ComputerDesktopIcon;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const items = [
    { to: '/settings', label: 'Settings', icon: Cog6ToothIcon },
    { to: '/support', label: 'Help & support', icon: QuestionMarkCircleIcon },
    ...((user?.is_staff || user?.is_superuser) ? [
      { to: '/admin/stats', label: 'Admin statistics', icon: ChartBarIcon, staff: true },
      { to: '/admin/questions', label: 'Questions', icon: DocumentTextIcon, staff: true },
      { to: '/admin/coupons', label: 'Coupons', icon: TicketIcon, staff: true },
    ] : []),
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 h-16 border-b border-white/10 bg-slate-950/95 backdrop-blur">
      <div className="flex h-full items-center gap-2 px-2 sm:px-4">
        {!focus && (
          <button onClick={onMenu} className="rounded-lg p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white" aria-label="Toggle menu">
            <Bars3Icon className="h-6 w-6" />
          </button>
        )}
        <Link to="/dashboard" className="ml-1 flex items-center gap-2.5">
          <img src={LOGO_URL} alt="" className="h-8 w-8 object-contain" />
          <span className="text-lg font-semibold tracking-tight text-white sm:text-xl">AirBrief</span>
        </Link>

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <Link to="/store" className="hidden items-center gap-2 rounded-lg border border-white/15 px-3 py-1.5 text-sm font-medium text-gray-200 transition-colors hover:bg-white/10 hover:text-white sm:inline-flex">
            <ShoppingBagIcon className="h-4 w-4" /> Plans
          </Link>
          <button
            onClick={() => changeTheme(THEME_ORDER[(THEME_ORDER.indexOf(theme) + 1) % THEME_ORDER.length])}
            className="rounded-lg p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
            title={`Theme: ${theme}. Click to change`}
            aria-label="Change theme"
          >
            <ThemeIcon className="h-5 w-5" />
          </button>

          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              className="flex items-center gap-2 rounded-lg p-1 pr-2 text-left transition-colors hover:bg-white/10"
            >
              {user?.avatar
                ? <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">{initials(user)}</span>}
              <span className="hidden min-w-0 sm:block">
                <span className="block max-w-[12rem] truncate text-sm font-medium text-white">{user?.full_name || 'Account'}</span>
                <span className="block max-w-[12rem] truncate text-xs text-gray-400">{user?.email}</span>
              </span>
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-60 overflow-hidden rounded-xl border border-white/10 bg-slate-900 py-1 shadow-2xl">
                {items.map((m, i) => (
                  <React.Fragment key={m.to}>
                    {m.staff && !items[i - 1]?.staff && <div className="my-1 border-t border-white/10" />}
                    <Link to={m.to} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white">
                      <m.icon className="h-4 w-4" />{m.label}
                    </Link>
                  </React.Fragment>
                ))}
                <button onClick={logout} className="mt-1 flex w-full items-center gap-3 border-t border-white/10 px-4 py-2 text-sm text-rose-400 hover:bg-rose-500/10">
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header2;
